import type { CityConfig } from "../city-template/types";

export const phoenixConfig: CityConfig = {
  city: "Phoenix",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "phoenix",
  route: "/cities/phoenix",
  title: "Emergency Passport Phoenix, AZ — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Phoenix, AZ — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving the nation's fastest-growing metro, Sky Harbor travelers, snowbirds, ASU students, aerospace professionals, and Arizona's international communities.",
  keywords: "emergency passport Phoenix AZ, expedited passport Phoenix, fast passport renewal Phoenix, same day passport Arizona, passport courier Phoenix, Sky Harbor passport, ASU study abroad passport, snowbird passport renewal, Maricopa County passport services, urgent passport Scottsdale Mesa Chandler",
  h1: "Emergency & Expedited Passport Services in Phoenix, AZ",
  heroSubtitle: "Nonstop to London departing from Sky Harbor in two weeks? ASU study abroad program confirmed for Barcelona? Snowbird season ending and passport expired for the flight home? We help Phoenix residents — from Desert Ridge families to Arcadia executives to Ahwatukee retirees — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20panoramic%20view%20of%20downtown%20Phoenix%20Arizona%20skyline%20at%20golden%20sunset%20with%20Camelback%20Mountain%20visible%20in%20background%2C%20modern%20glass%20skyscrapers%20reflecting%20warm%20desert%20light%2C%20dramatic%20Sonoran%20Desert%20mountain%20silhouette%2C%20professional%20architectural%20photography%20with%20rich%20warm%20amber%20and%20terracotta%20tones%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=phoenix-hero-2026&orientation=landscape",
  heroImageAlt: "Phoenix Arizona downtown skyline with Camelback Mountain at golden sunset",
  geoPosition: "33.4484;-112.0740",
  geoPlacename: "Phoenix, Arizona",
  lastModified: "2026-07-18",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 115 miles — about 1 hour 45 minutes via I-10. The nearest passport agency to Phoenix.",
  },
  nearbyCitiesZips: [
    { name: "Phoenix", zips: "85001–85099" },
    { name: "Scottsdale", zips: "85250–85271" },
    { name: "Mesa", zips: "85201–85215" },
    { name: "Chandler", zips: "85224–85286" },
    { name: "Gilbert", zips: "85233–85298" },
    { name: "Glendale", zips: "85301–85318" },
    { name: "Tempe", zips: "85280–85288" },
    { name: "Peoria", zips: "85345–85383" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping to send your documents directly to us — we handle the rest." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed." },
  ],
  internalLinks: [
    { name: "Emergency passport in Scottsdale, AZ", route: "/cities/scottsdale" },
    { name: "Expedited passport in Mesa, AZ", route: "/cities/mesa" },
    { name: "Fast passport renewal in Chandler, AZ", route: "/cities/chandler" },
    { name: "Quick passport services in Gilbert, AZ", route: "/cities/gilbert" },
    { name: "Urgent passport in Tempe, AZ", route: "/cities/tempe" },
    { name: "Emergency passport in Glendale, AZ", route: "/cities/glendale-az" },
    { name: "Expedited passport in Peoria, AZ", route: "/cities/peoria-az" },
    { name: "Arizona passport services overview", route: "/states/arizona" },
  ],
  fpcOfficeAddress: "4742 N 24th St, Phoenix, AZ 85016",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your home or office in most cases, ship with our prepaid label from any UPS/FedEx location, or schedule a drop-off at our Phoenix office. Most clients ship overnight — no visit required.",
  officePhone: "(877) 253-0084",
  officeHours: [
    { days: "Monday – Friday", hours: "9:00 AM – 6:00 PM MST" },
    { days: "Saturday", hours: "10:00 AM – 2:00 PM MST" },
    { days: "Sunday", hours: "Closed" },
  ],
  officePhotoUrl: "https://storage.readdy-site.link/project_files/5b15eb5b-af79-4c03-9ad0-73c6095d69a7/39b34e24-6cae-4aa4-bcd8-dc68c850858b_compressed_phoenix-az---office.webp",
  officePhotoAlt: "Fast Passport Center office building in Phoenix, Arizona — modern multi-story glass and stone professional building at 4742 N 24th St in the Camelback East corridor",
  fpcOfficeDistance: "4742 N 24th St — Phoenix (Camelback East)",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Phoenix and the greater Valley of the Sun, with a local office at 4742 N 24th St. From the downtown business district to the East Valley suburbs of Mesa, Chandler, and Gilbert, from the West Valley communities of Glendale, Peoria, and Surprise to Scottsdale and Paradise Valley — we serve all of Maricopa County and beyond. Visit our Phoenix office by appointment for full-service expedited and emergency passport processing.",
  nearbyCitiesIntro: "We serve Phoenix and all Valley communities including Scottsdale, Mesa, Chandler, Gilbert, Glendale, Tempe, Peoria, Surprise, Goodyear, Avondale, and Buckeye. We also serve travelers departing from Phoenix Sky Harbor International Airport who need emergency passports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Phoenix as simple as possible — with the efficiency and reliability Valley executives, Arizona families, and ASU students need. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Phoenix travelers start online and ship documents overnight — no office visit required for most services.",
  mistakesIntro: "Phoenix travelers encounter unique passport issues — everything from snowbird season passport expiration surprises to 6-month validity rules for Mexico crossings. Here are the most common passport mistakes we see from this area.",
  testimonialsIntro: "Phoenix travelers — from Arcadia executives to Desert Ridge families to ASU students — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Phoenix, AZ? Here are the most common ones we hear from Valley-area travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  servicesIntro: "Whether you need a renewal before a PHX flight to London, an emergency passport for a last-minute aerospace conference, or a first-time passport for an ASU study abroad program — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://azdot.gov/mvd",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins accepted", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "PHX flight to London in two days? Aerospace conference in Frankfurt this week? Our emergency courier service submits directly via reserved agency slots — as fast as 24 hours for Phoenix-area travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Phoenix residents renew fast — no agency visit required for most renewals. Perfect for Valley executives, snowbirds, and Arizona families preparing for international travel.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide Phoenix residents through every step — from Ahwatukee to Scottsdale to Surprise — with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your PHX international connection or Mexico weekend trip is not derailed.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear instructions and checklists so your family vacation or Arizona family trip abroad stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations. Common for Phoenix professionals after name changes.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for Phoenix aerospace executives, tech workers, and consultants who travel internationally while their primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. We guide Valley-area travelers through the process — from poolside water damage to coffee spills on the home office desk.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. Essential for Phoenix residents who frequently cross into Mexico at Nogales or drive to Rocky Point.",
      bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Phoenix AZ", description: "Urgent passport processing for Phoenix Arizona travelers with immediate departure needs from Sky Harbor Airport." },
    { name: "Expedited Passport Renewal Phoenix AZ", description: "Fast passport renewal for Phoenix Arizona residents with a previously-issued passport." },
    { name: "First-Time Passport Phoenix AZ", description: "New passport application services for Phoenix Arizona residents applying for their first passport." },
    { name: "Child Passport Phoenix AZ", description: "Minor passport services for Phoenix Arizona families with children under 16." },
    { name: "Name Change Passport Phoenix AZ", description: "Passport name correction after marriage, divorce, or legal name changes for Phoenix residents." },
    { name: "Second Passport Phoenix AZ", description: "Second passport services for Phoenix aerospace executives and frequent international travelers." },
  ],
  testimonials: [
    {
      name: "Dr. Amir K.",
      location: "Phoenix, AZ (Honeywell Aerospace)",
      rating: 5,
      text: "I'm a program manager at Honeywell and travel quarterly to our partners in the UK, Germany, and Japan. Fast Passport Center has handled three of my renewals and one emergency replacement over the past six years. They are hands-down the most reliable passport service in Phoenix.",
      service: "Multiple Renewals",
    },
    {
      name: "Sophia R.",
      location: "Tempe, AZ (ASU)",
      rating: 5,
      text: "I was accepted into ASU's semester abroad program in Barcelona with six weeks before departure. My passport had expired during COVID. Fast Passport Center renewed it in seven business days. I lived in Barcelona for four months. Thank you for making it possible.",
      service: "Study Abroad Renewal",
    },
    {
      name: "The Nguyen Family",
      location: "Chandler, AZ",
      rating: 5,
      text: "We travel to Ho Chi Minh City regularly to visit relatives. When two of our three children's passports expired before a family wedding trip, Fast Passport Center processed all three simultaneously in eight business days. The wedding was beautiful.",
      service: "Multiple Child Renewals",
    },
  ],
  facilities: [
    {
      id: "phx-1",
      name: "Phoenix Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4949 E Van Buren St",
      city: "Phoenix",
      state: "AZ",
      zip: "85026",
      phone: "(602) 225-3164",
      hours: "Mon–Fri 9:00 AM – 5:00 PM (appointment required)",
      distance: "Central Phoenix",
      website: "https://tools.usps.com/rcas.htm",
      websiteLabel: "USPS Location",
      notes: "Large central Phoenix facility serving the downtown and midtown corridor. Passport acceptance by appointment — call ahead for availability. Photo services on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "phx-2",
      name: "Biltmore Area Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3713 E Indian School Rd",
      city: "Phoenix",
      state: "AZ",
      zip: "85018",
      phone: "(602) 956-4882",
      hours: "Mon–Fri 9:00 AM – 5:00 PM",
      distance: "Biltmore / Arcadia area",
      website: "https://tools.usps.com/rcas.htm",
      websiteLabel: "USPS Location",
      notes: "Convenient for Arcadia, Biltmore, and Camelback corridor residents. Passport photos available. Call ahead for weekend availability.",
      icon: "ri-mail-line",
    },
    {
      id: "phx-3",
      name: "North Phoenix Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "19820 N 7th St",
      city: "Phoenix",
      state: "AZ",
      zip: "85024",
      phone: "(602) 569-4400",
      hours: "Mon–Fri 9:00 AM – 5:00 PM (appointment required)",
      distance: "Desert Ridge / North Phoenix",
      website: "https://tools.usps.com/rcas.htm",
      websiteLabel: "USPS Location",
      notes: "Serves Desert Ridge, Anthem, and North Phoenix communities. Call ahead for passport appointment availability — limited slots during snowbird season.",
      icon: "ri-mail-line",
    },
    {
      id: "phx-4",
      name: "Ahwatukee Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4450 E Ray Rd",
      city: "Phoenix",
      state: "AZ",
      zip: "85044",
      phone: "(480) 598-4050",
      hours: "Mon–Fri 9:00 AM – 5:00 PM",
      distance: "Ahwatukee / South Mountain",
      website: "https://tools.usps.com/rcas.htm",
      websiteLabel: "USPS Location",
      notes: "Serves Ahwatukee Foothills and South Mountain communities. Call ahead for appointment — smaller facility with limited daily slots.",
      icon: "ri-mail-line",
    },
    {
      id: "phx-5",
      name: "Maricopa County Clerk — Downtown",
      type: "County Clerk Passport Acceptance",
      address: "201 W Jefferson St",
      city: "Phoenix",
      state: "AZ",
      zip: "85003",
      phone: "(602) 37-CLERK",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      distance: "Downtown Phoenix",
      website: "https://www.maricopa.gov/588/Passports",
      websiteLabel: "Maricopa County Passports",
      notes: "Primary county-level acceptance facility for Maricopa County. Appointment recommended. Photo services not available — bring your own photos.",
      icon: "ri-government-line",
    },
    {
      id: "phx-6",
      name: "Scottsdale Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1776 N Scottsdale Rd",
      city: "Scottsdale",
      state: "AZ",
      zip: "85257",
      phone: "(480) 941-1280",
      hours: "Mon–Fri 9:00 AM – 5:00 PM (appointment required)",
      distance: "Scottsdale",
      website: "https://tools.usps.com/rcas.htm",
      websiteLabel: "USPS Location",
      notes: "Convenient for Scottsdale, Paradise Valley, and North Tempe residents. Passport photos available. Saturday appointments may be available — call to confirm.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — typically booked 2+ weeks out, walk-ins not accepted", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — in-person visit at Western Passport Center in Tucson (~115 mi via I-10); as a registered courier, we handle this submission for you", fpc: "Most Phoenix travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility with proof of travel", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public — agency serves emergencies only", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Phoenix-Specific Guidance", agency: "Generic — no Sky Harbor, aerospace, or ASU student expertise", fpc: "Full PHX airport, aerospace/ASU/snowbird knowledge built in" },
  ],
  comparisonExtraColumns: undefined,
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Assuming You Can Walk Into a Regional Agency",
      description: "The nearest regional passport agency is the Western Passport Center in Tucson — about 115 miles from Phoenix via I-10. It requires documented proof of urgent travel within 3 business days for walk-in access. It is not a general drop-in service for expedited needs. Our courier channel bypasses this entirely.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Snowbird Passport Expiration Surprises",
      description: "Hundreds of thousands of seasonal residents discover expired passports when booking return flights to Canada or planning Mexico winter getaways. Check your passport validity before you leave your summer home — renewals take longer when you are already in Arizona.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Mexico 6-Month Validity Rule at Some Entry Points",
      description: "While Mexico does not universally enforce the 6-month validity rule, some airports and border crossings do. A passport expiring three months after your Cancún return may be denied. We flag this immediately.",
    },
    {
      icon: "ri-error-warning-line",
      title: "ASU Students Waiting Until the Week Before Departure",
      description: "Study abroad program acceptance happens months before departure. Passport applications should begin the day you accept your program — not when departure approaches.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations at Valley Drugstores",
      description: "Many Phoenix pharmacies and big-box stores produce photos that fail State Department standards — wrong background shade, shadows, or sizing issues. We catch this in review before it causes rejection.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Missing Parental Consent for Minors",
      description: "Arizona families frequently overlook the DS-3053 consent form when one parent is unavailable. This is an automatic rejection — we guide you through it correctly.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home, office, or dorm." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your travel situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Phoenix clients ship from home — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you benefit from our reserved passport agency availability without the need for an in-person agency visit." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up — just certainty." },
  ],
  faqs: [
    { q: "How fast can I get a passport in Phoenix?", a: "We offer Phoenix-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 2–9 business days. We review your documents before submission to catch errors that cause delays." },
    { q: "Do I need to visit a Phoenix office?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (a Phoenix post office or county clerk), then ship documents overnight to our processing center. We handle the State Department submission and return your passport to your Phoenix address." },
    { q: "Where can I get a passport acceptance in Phoenix?", a: "Phoenix has acceptance facilities at many post offices (downtown, Biltmore, North Phoenix, Ahwatukee) as well as the Maricopa County Clerk and surrounding city post offices in Scottsdale, Mesa, Chandler, and Glendale. We maintain an updated list of Valley facilities." },
    { q: "Does the Western Passport Center in Tucson handle walk-ins?", a: "The Western Passport Center in Tucson requires appointments for life-or-death emergency travel only. Walk-ins are rarely accepted. For travel within 3–14 days, our registered courier service is the fastest legally available channel without agency restrictions." },
    { q: "What is the best price for expedited passport service in Phoenix?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
    { q: "I'm an ASU student studying abroad. How do I get my passport quickly?", a: "Study abroad students should apply for passports the moment their program is confirmed — not when departure approaches. Ship to Fast Passport Center and receive in 2–9 business days. First-time applicants need the DS-11 form and an acceptance clerk visit at an ASU-area post office." },
    { q: "Can Fast Passport Center process multiple family passports at once?", a: "Yes — we process all family passports simultaneously. Each family member needs their own form and fee. We'll keep you updated on all applications at once. Particularly useful for Phoenix families planning international vacations or visiting relatives abroad." },
    { q: "I'm a snowbird — can I renew my Canadian or U.S. passport while staying in Phoenix?", a: "For U.S. passports — absolutely, our service works perfectly for seasonal residents with a Phoenix mailing address. We process renewals in 2–9 business days. For Canadian passports, you will need to contact the Canadian consulate — we only process U.S. passports." },
    { q: "How do I check my Phoenix passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from Phoenix to final return shipping typically matches your chosen speed tier." },
    { q: "Can you help with passports for Sky Harbor International Airport travel?", a: "Yes. PHX is one of the busiest airports in the American Southwest, and we regularly assist Phoenix travelers with expedited passports for international departures. We understand the Valley's airport landscape — from PHX international nonstops to connecting flights through LAX and DFW." },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Phoenix Residents",
    intro: "Phoenix's unique position as America's fastest-growing metro and a Sonoran Desert crossroads creates distinct passport timing challenges. From snowbird seasonal surges to aerospace corporate travel to ASU study abroad deadlines — here's what Phoenix travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "PHX — Phoenix Sky Harbor International Airport",
        subtitle: "America's Friendliest Airport, 22M+ annual passengers, nonstop international routes",
        description: "Sky Harbor connects Phoenix to the world with nonstop flights to London Heathrow, Frankfurt, Tokyo Narita, Mexico City, Cancún, and 100+ domestic destinations. As a major hub for American Airlines and Southwest, PHX handles a diverse mix of business travelers, snowbirds, students, and international families. The airport's single-terminal convenience belies its global reach — a nonstop to London departing in 10 days means a passport emergency needs solving now, not next week.",
        tip: "PHX international nonstops require valid passports at check-in. Many Phoenix travelers book flights months ahead but do not check passport validity until the week before departure. Always verify your passport expiration the moment you book an international flight.",
      },
      {
        icon: "ri-building-2-line",
        title: "Aerospace, Defense & Semiconductor — Phoenix's Fortune 500 Corridor",
        subtitle: "Honeywell Aerospace HQ, Boeing, Raytheon, Intel, Microchip Technology",
        description: "Phoenix has quietly become one of America's most important aerospace and semiconductor manufacturing centers. Honeywell Aerospace is headquartered here. Boeing and Raytheon maintain major facilities employing thousands. Intel's Ocotillo campus is one of the most advanced semiconductor fabrication plants on Earth. Defense program managers, aerospace engineers, and semiconductor supply chain executives travel regularly to partners in the UK, Germany, Israel, Japan, Taiwan, and Singapore — often on 1–3 week booking windows.",
        tip: "Aerospace and defense contractors should maintain current passports year-round. International site visits, supplier meetings, and trade shows often materialize with 1–2 weeks' notice — standard government processing cannot keep pace.",
      },
      {
        icon: "ri-sun-line",
        title: "Snowbird & Seasonal Resident Passport Dynamics",
        subtitle: "400,000+ seasonal residents — one of the largest seasonal population shifts in America",
        description: "Phoenix's snowbird population — primarily from Canada, the Midwest, and the Pacific Northwest — creates massive seasonal passport demand spikes between October and April. Seasonal residents frequently discover expired passports when booking return flights or planning Mexico getaways from their Arizona winter base. The volume of snowbird passport renewals creates a unique urgency pattern: hundreds of thousands of travelers all needing documentation within a compressed October-to-January window.",
        tip: "Check your passport validity before you leave your summer home, not after you arrive in Phoenix. Renewals from within Arizona are perfectly manageable — but the processing clock starts from when you apply, not when you remember.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "ASU Study Abroad & University International Travel",
        subtitle: "Arizona State University — 140,000+ students, 400+ study abroad programs",
        description: "ASU is the largest public university in the United States by enrollment, with a massive international footprint. The university sends thousands of students abroad annually to programs in 65+ countries and hosts international students from every continent. Study abroad departures create seasonal passport demand surges in January (spring programs) and May (summer/fall programs). First-generation college students at ASU are often first-time passport applicants navigating an unfamiliar process on a fixed deadline.",
        tip: "ASU study abroad programs have fixed departure dates that do not move for passport delays. Students should start passport applications the moment their program is confirmed — not when departure approaches. First-time applicants need in-person acceptance clerk visits, which adds lead time.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "Phoenix & Cross-Border Mexico Travel",
      description: "Phoenix is just 180 miles from the Mexico border — Rocky Point (Puerto Peñasco) is Phoenicians' beach town, Nogales is a major crossing point, and weekend trips to Mexico are part of Valley life. Phoenix's large Hispanic population maintains strong cross-border family ties, driving constant passport and passport card demand. Mexico is the number one international destination from Sky Harbor.",
    },
  },
  destinations: {
    title: "Where Phoenix Travelers Go — And Why Passports Get Urgent",
    intro: "Phoenix's position as a Sun Belt mega-city with deep ties to Mexico, strong aerospace connections to Europe and Asia, and a massive seasonal population creates a unique international travel profile.",
    stats: [
      { stat: "38%", label: "of Phoenix expedited requests are Mexico-related — family visits, Rocky Point weekends, resort travel, and cross-border business" },
      { stat: "1–3 Weeks", label: "average lead time Phoenix travelers have from trip booking to international departure" },
      { stat: "Oct–Jan", label: "peak season when snowbird arrivals, holiday travel, and ASU spring study abroad deadlines collide" },
      { stat: "24 Hours", label: "shortest turnaround we have accommodated for a Honeywell engineer departing to London" },
    ],
    destinations: [
      { name: "Mexico", context: "Rocky Point beach weekends, Puerto Peñasco family getaways, Nogales border crossing, Cancún and Cabo resort vacations, Guadalajara and Mexico City heritage travel, Sonoran business corridor", urgency: "Rocky Point trips planned 1–3 weeks ahead; resort bookings made months ahead — passport must be valid throughout", icon: "ri-map-pin-2-line" },
      { name: "United Kingdom & Europe", context: "British Airways PHX-London nonstop, aerospace business travel, ASU study abroad programs in Spain, Italy, France, Germany, snowbird heritage trips", urgency: "Business travel often confirmed 1–4 weeks before departure; Schengen Zone requires valid passport", icon: "ri-map-pin-2-line" },
      { name: "Japan & Asia Pacific", context: "All Nippon Airways PHX-Tokyo nonstop, semiconductor supply chain (Intel, TSMC), Honeywell Asia-Pacific operations, ASU research partnerships with Japan and Korea", urgency: "Asia Pacific business travel often arranged 2–6 weeks ahead — 6-month passport validity is critical", icon: "ri-map-pin-2-line" },
      { name: "Germany & Central Europe", context: "Condor PHX-Frankfurt nonstop, aerospace supplier meetings, defense industry travel, German heritage tourism among Phoenix's large German-American community", urgency: "Trade show and conference dates are fixed and unmovable — passport must be ready when the event is", icon: "ri-map-pin-2-line" },
      { name: "Canada", context: "Snowbird return travel, Vancouver and Calgary family visits, Banff and Canadian Rockies summer escapes from Arizona heat, cross-border business", urgency: "Snowbird seasonal returns create predictable annual demand — but many forget to check validity until weeks before departure", icon: "ri-map-pin-2-line" },
      { name: "Caribbean & Latin America", context: "Cancún, Dominican Republic, and Jamaica resort packages, Central America heritage travel, cruise departures from California and Florida ports, Costa Rica eco-tourism", urgency: "Last-minute all-inclusive deals and cruise bookings create 2–4 week urgency windows", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other Arizona Service Areas",
    crossLinksDesc: "We serve travelers across the Grand Canyon State with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "Scottsdale", route: "/cities/scottsdale" },
      { name: "Mesa", route: "/cities/mesa" },
      { name: "Chandler", route: "/cities/chandler" },
      { name: "Gilbert", route: "/cities/gilbert" },
      { name: "Tempe", route: "/cities/tempe" },
      { name: "Glendale", route: "/cities/glendale-az" },
      { name: "Peoria", route: "/cities/peoria-az" },
    ],
  },
  photoLocations: [
    { name: "CVS Pharmacy — Biltmore", address: "2505 E Camelback Rd, Phoenix, AZ 85016", hours: "Daily 7 AM – 11 PM", website: "https://www.cvs.com/photo" },
    { name: "Walgreens — Arcadia", address: "3524 E Indian School Rd, Phoenix, AZ 85018", hours: "Call for hours", website: "https://www.walgreens.com/photo" },
    { name: "CVS Pharmacy — Desert Ridge", address: "5350 E Deer Valley Dr, Phoenix, AZ 85054", hours: "Daily 7 AM – 11 PM", website: "https://www.cvs.com/photo" },
    { name: "The UPS Store — Scottsdale", address: "7373 N Scottsdale Rd, Scottsdale, AZ 85253", hours: "Call for hours", website: "https://www.theupsstore.com/print/passport-photos" },
    { name: "Walgreens — Ahwatukee", address: "5035 E Ray Rd, Phoenix, AZ 85044", hours: "Call for hours", website: "https://www.walgreens.com/photo" },
    { name: "FedEx Office — Tempe", address: "930 E University Dr, Tempe, AZ 85281", hours: "Call for hours", website: "https://www.office.fedex.com/default/office" },
    { name: "CVS Pharmacy — Glendale", address: "5820 W Peoria Ave, Glendale, AZ 85302", hours: "Daily 7 AM – 11 PM", website: "https://www.cvs.com/photo" },
    { name: "Walgreens — Chandler", address: "2945 S Alma School Rd, Chandler, AZ 85286", hours: "Call for hours", website: "https://www.walgreens.com/photo" },
  ],
  dmvLocations: [
    {
      name: "Arizona MVD — Phoenix Central",
      address: "4005 N 51st Ave, Phoenix, AZ 85031",
      hours: "Mon–Fri, 7:30 AM – 4:45 PM",
      note: "Driver licenses, REAL ID, vehicle registration. Appointment strongly recommended.",
      featured: true,
    },
    {
      name: "Arizona MVD — Scottsdale",
      address: "7339 E Paradise Ln, Scottsdale, AZ 85260",
      hours: "Mon–Fri, 7:30 AM – 4:45 PM",
      note: "Full-service MVD office. REAL ID available. More appointment availability than Phoenix central locations.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "About 3 miles from downtown Phoenix",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's primary international airport — 22M+ annual passengers. Nonstop routes to London Heathrow, Frankfurt, Tokyo Narita, Mexico City, Cancún, and 100+ domestic destinations. Major hub for American Airlines and Southwest.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport Website",
      featured: true,
    },
    {
      code: "DVT",
      name: "Phoenix Deer Valley Airport",
      distance: "About 20 miles north of downtown Phoenix",
      address: "702 W Deer Valley Rd, Phoenix, AZ 85027",
      note: "One of the nation's busiest general aviation airports. Corporate and private jet operations serving North Phoenix and Scottsdale executive community.",
      flightUrl: "https://www.phoenix.gov/aviation/deer-valley",
      flightLabel: "Deer Valley Airport Website",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the Arizona Department of Health Services Vital Records. Online, mail, or in-person at the Phoenix vital records office. Serving Maricopa County and all Arizona counties.",
      url: "https://www.azdhs.gov/licensing/vital-records/",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through the Arizona Department of Health Services Vital Records. Required for estate and legal matters in Maricopa County.",
      url: "https://www.azdhs.gov/licensing/vital-records/",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request certified marriage records from the Maricopa County Clerk of the Superior Court. Required for name change passport applications.",
      url: "https://www.clerkofcourt.maricopa.gov/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Maricopa County Superior Court Clerk. Required for name change passport corrections.",
      url: "https://www.clerkofcourt.maricopa.gov/",
    },
  ],
  exploreNearbyHeading: "Explore Nearby City Pages",
  areaServedList: [
    { "@type": "City", "name": "Phoenix" },
    { "@type": "City", "name": "Scottsdale" },
    { "@type": "City", "name": "Mesa" },
    { "@type": "City", "name": "Chandler" },
    { "@type": "City", "name": "Gilbert" },
    { "@type": "City", "name": "Glendale" },
    { "@type": "City", "name": "Tempe" },
    { "@type": "City", "name": "Peoria" },
    { "@type": "AdministrativeArea", "name": "Maricopa County" },
  ],
  serviceAreaDesc: "Fast Passport Center has a physical office at 4742 N 24th St in Phoenix's Camelback East corridor, providing emergency and expedited passport services as fast as 24 hours. A+ BBB rated registered U.S. Department of State passport courier serving Phoenix, Scottsdale, Mesa, Chandler, Gilbert, Glendale, Tempe, Peoria, and all Maricopa County communities.",
  landmarks: [
    { name: "Sky Harbor Airport (PHX)", distance: "~5 miles", icon: "ri-plane-line", desc: "Phoenix's primary international airport — 22M+ annual passengers, nonstops to London, Frankfurt, Tokyo, Mexico City" },
    { name: "I-10 / I-17", distance: "Adjacent", icon: "ri-road-map-line", desc: "Major interstates intersecting through Phoenix — primary north-south and east-west corridors" },
    { name: "Arizona State Capitol", distance: "Downtown", icon: "ri-government-line", desc: "State government offices at 1700 W Washington St" },
    { name: "Chase Field", distance: "Downtown", icon: "ri-trophy-line", desc: "Arizona Diamondbacks MLB ballpark — 401 E Jefferson St" },
    { name: "Footprint Center", distance: "Downtown", icon: "ri-basketball-line", desc: "Phoenix Suns & Mercury arena — 201 E Jefferson St" },
    { name: "Desert Botanical Garden", distance: "~5 miles", icon: "ri-landscape-line", desc: "1201 N Galvin Pkwy — 50,000+ desert plants on 140 acres" },
    { name: "Camelback Mountain", distance: "~8 miles", icon: "ri-image-line", desc: "Iconic Phoenix hiking landmark — Echo Canyon & Cholla Trails" },
    { name: "ASU Downtown Campus", distance: "Downtown", icon: "ri-graduation-cap-line", desc: "Arizona State University downtown Phoenix campus — 500+ programs" },
  ],
};